import { eachDayOfInterval } from 'date-fns';
import {
  add,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { call, fork, put, race, take, takeEvery } from 'redux-saga/effects';

import { FavoriteItem } from '../domain/favorites';
import {
  asKiSyokenResolvedSearchParams,
  buildKiSyokenResolvedSearchParams,
  resolveKiSyokenShuCondition,
} from '../domain/kiSyoken/buildResolvedSearchParams';
import { filteredKiSyokenDateRanges } from '../domain/kiSyoken/consistent';
import {
  makeKiSyokenDateFromDateRange,
  makeKiSyokenDateFromDateRangeWithoutCustom,
} from '../domain/kiSyoken/date';
import {
  defaultDateRangeParams,
  defaultExcludeToday,
} from '../domain/kiSyoken/defaultValue';
import {
  FilteredKiSyokenComparisonSection,
  FilteredKiSyokenDateRange,
  KiSyokenDateRangeParams,
  KiSyokenFavorite,
  KiSyokenSearchParams,
} from '../domain/kiSyoken/types';
import { validateKiSyokenParams } from '../domain/kiSyoken/validation';
import {
  mergeYmdListsForWeekdayHolidaySeparation,
  normalizeWeekdayHolidayYmdListsForUiState,
} from '../domain/kiSyoken/weekdayHolidayDate';
import { KiTagListOptions } from '../domain/schemas';

// useDate共通関数のインポート
import {
  calculateComparisonPeriod,
  calculateMonthComparisonPeriodInUseDate,
  isComparisonPeriodRecalculationNeeded,
} from '../components/organisms/KiSyokenSearchForm/useDateComponents';

import {
  DataKiSyokenActionCreators,
  DataKiSyokenActionTypes,
  dataKiSyokenDataSelector,
  dataKiSyokenLoadingStateSelector,
} from '../redux/server/dataKiSyoken';
import { dataColumnsSwappedSelector } from '../redux/server/dataKiSyokenFilterData';
import { DataKiSyokenKasidamaActionCreators } from '../redux/server/dataKiSyokenKasidama';
import {
  SettingsNationalHolidaysActionCreators,
  SettingsNationalHolidaysActionTypes,
  settingsNationalHolidaysSelector,
} from '../redux/server/settingsNationalHolidays';
import {
  SettingsOptionsKiSyokenActionCreators,
  SettingsOptionsKiSyokenActionTypes,
  settingsOptionsKiSyokenFieldsSelector,
  settingsOptionsKiSyokenKiTagListSelector,
  settingsOptionsKiSyokenLoadingStateSelector,
  settingsOptionsKiSyokenSearchConditionSelector,
} from '../redux/server/settingsOptionsKiSyoken';
import { ShortenedUrlActionCreators } from '../redux/server/shortenedUrl';
import {
  SaveAsKiSyokenFavoriteAction,
  SaveKiSyokenFavoriteAction,
  SearchActionKiListAverageAction,
  SearchActionKiTagAverageAction,
  SearchActionKiTagAverageOnSearchConditionChangeAction,
  kiSyokenKasidamaColumnsOrderSelector,
  kiSyokenListFilterSelector,
  kiSyokenMksHallCodesSelector,
  kiSyokenNameFilterSelector,
  kiSyokenSelectCheckedKiListSelector,
  kiSyokenSelectedFavoritePageSettingSelector,
  kiSyokenSelectedFavoriteSelector,
  kiSyokenSelectedTableFilterSelector,
  kiSyokenSettingSelector,
  kiSyokenShowKiTagLabelSelector,
  kiSyokenSubMenuNumberFilterSelector,
  kiSyokenTableHeaderSubMenuMultiFilterSelector,
  kiSyokenTagListSelector,
} from '../redux/ui/kiSyokenSettings';
import {
  ChangeKiSyokenFavoriteAction,
  ChangeKiSyokenFieldsAction,
  CreateKiSyokenShortenedUrlAction,
  KiSyokenSettingActionCreators,
  KiSyokenSettingActionTypes,
  SearchKiSyokenSettingsAction,
  TriggerKiSyokenSwapFieldsAction,
  changeKiSyokenCustomDateAction,
  kiSyokenColumnsOrderSelector,
  kiSyokenCurrentShuSelector,
  kiSyokenCustomDateSelector,
  kiSyokenSearchParamsSelector,
  kiSyokenSelectedDateRangeParamsSelector,
  kiSyokenSelectedFavoriteDataSelector,
  searchActionKiTagAverageOnSearchConditionChangeAction,
} from '../redux/ui/kiSyokenSettings';
import { SettingsFavoritesActionCreators } from '../redux/ui/settingsFavorites';
import { UserActionTypes, userSelector } from '../redux/ui/user';
import { compressToEncodedURIComponent } from '../utils/compressToEncodedURIComponent';
import { customSelect } from '../utils/customSelect';
// 分離ユーティリティ（重複しない正規のインポート）
import { excludeHoliday } from '../utils/excludeHoliday';
import { excludeWeekday } from '../utils/excludeWeekday';
import { buildColumnsOrder } from '../utils/fieldsOrder';
import { formatWeekdayAndHoliday } from '../utils/formatWeekdayAndHoliday';
import { recalcColumnOrder } from '../utils/orderedCell';
import { retrieveMatchedIdLists } from '../utils/retrieveMatchedIdLists';
import { sanitizeKiSyokenFavoriteForRestrictedKyoyu } from '../utils/sanitizeFavoritesForRestrictedKyoyuGroup';
import { selectShu2SearchCondition } from '../utils/shu';
import { multiFilterFromFavoriteWithType } from '../utils/tableHeaderSubMenuMultiFilter';
import { ensureUserLoadedForSaga } from './ensureUserLoadedForSaga';

/**
 * 祝日データを取得し、必要に応じてAPIリクエストを実行する
 */
function* fetchHolidaysDataSaga() {
  const holidaysData = yield* customSelect(settingsNationalHolidaysSelector);
  if (!holidaysData) {
    yield put(
      SettingsNationalHolidaysActionCreators.fetchSettingsNationalHolidaysAction()
    );
    yield take(
      SettingsNationalHolidaysActionTypes.FETCH_SETTINGS_NATIONAL_HOLIDAYS_SUCCESS
    );
  }
  const updated = yield* customSelect(settingsNationalHolidaysSelector);
  if (!updated) {
    throw new Error('updatedNationalHolidays is not found');
  }
  return updated.holidays;
}

/**
 * 平日/土日祝比較の日付分離処理を実行する
 */
function* processWeekdayHolidaySeparationSaga(
  searchCondition: KiSyokenSearchParams,
  selectedDateRangeParams: KiSyokenDateRangeParams
) {
  const storedYmdList = searchCondition.ymdList ?? [];
  const storedYmdComparisonList = searchCondition.ymdComparisonList ?? [];

  // 平日/土日祝比較の場合のみ祝日データを取得
  const holidays = yield* fetchHolidaysDataSaga();

  const mergedYmdStrings = mergeYmdListsForWeekdayHolidaySeparation(
    storedYmdList,
    storedYmdComparisonList
  );

  if (selectedDateRangeParams.dateUnit === '自由選択') {
    const dateList = mergedYmdStrings.map((v: string) => new Date(v));
    return formatWeekdayAndHoliday({
      ymdList: excludeHoliday(dateList, holidays).map((v: Date) =>
        format(v, 'yyyy-MM-dd')
      ),
      ymdComparisonList: excludeWeekday(dateList, holidays).map((v: Date) =>
        format(v, 'yyyy-MM-dd')
      ),
    });
  }

  // ymdListが無ければ start/end から生成（範囲選択など）
  let baseDateList: Date[] = [];
  if (mergedYmdStrings.length > 0) {
    baseDateList = mergedYmdStrings.map((dateStr: string) => new Date(dateStr));
  } else if (searchCondition.startDate && searchCondition.endDate) {
    baseDateList = eachDayOfInterval({
      start: new Date(searchCondition.startDate),
      end: new Date(searchCondition.endDate),
    });
  } else {
    throw new Error('ymdListまたはstartDate/endDateが指定されていません');
  }

  return formatWeekdayAndHoliday({
    ymdList: excludeHoliday(baseDateList, holidays).map((v: Date) =>
      format(v, 'yyyy-MM-dd')
    ),
    ymdComparisonList: excludeWeekday(baseDateList, holidays).map((v: Date) =>
      format(v, 'yyyy-MM-dd')
    ),
  });
}

/**
 * 日付パラメータを処理してAPI送信用のパラメータを生成する
 */
function* processDateParamsForApiSaga(
  searchCondition: KiSyokenSearchParams,
  selectedDateRangeParams: KiSyokenDateRangeParams
) {
  const isWeekdayHoliday =
    selectedDateRangeParams.comparisonSection === '平日/土日祝比較';

  let dateRangeParamsForApi = {};

  if (isWeekdayHoliday) {
    // 平日/土日祝比較の場合：祝日データを取得して分離処理
    dateRangeParamsForApi = yield* processWeekdayHolidaySeparationSaga(
      searchCondition,
      selectedDateRangeParams
    );
  } else {
    // 平日/土日祝比較以外：UI（useDate）の確定値を信頼してそのまま送る
    if (selectedDateRangeParams.dateUnit === '自由選択') {
      dateRangeParamsForApi = {
        ymdList: searchCondition.ymdList,
        ymdComparisonList: searchCondition.ymdComparisonList,
      };
    } else {
      dateRangeParamsForApi = {
        startDate: searchCondition.startDate,
        endDate: searchCondition.endDate,
        startComparisonDate: searchCondition.startComparisonDate,
        endComparisonDate: searchCondition.endComparisonDate,
      };
    }
  }

  // 日付関連パラメータを除外してAPI送信用オブジェクトを作成
  const {
    ymdList,
    ymdComparisonList,
    startDate,
    endDate,
    startComparisonDate,
    endComparisonDate,
    ...restParams
  } = searchCondition;

  return {
    ...restParams,
    ...dateRangeParamsForApi,
  };
}

/**
 * テーブルデータfetch用のラッパー関数。fetchしかつsearchParamsに値を設定する
 */
function* fetchDataKiSyokenSaga(originalSearchParams: KiSyokenSearchParams) {
  // APIリクエスト前のパラメータバリデーション
  validateKiSyokenParams(originalSearchParams);

  yield put(
    DataKiSyokenKasidamaActionCreators.hideDataKiSyokenKasidamaAction()
  );
  yield put(
    DataKiSyokenActionCreators.fetchDataKiSyokenAction(originalSearchParams)
  );
  // 平均合計行データをクリア（機種集計と同じパターン）
  yield put(DataKiSyokenActionCreators.resetDataKiSyokenKiListAverageAction());
  yield put(
    DataKiSyokenActionCreators.clearDataKiSyokenFilteredTagAverageAction()
  );
  yield put(DataKiSyokenActionCreators.clearDataKiSyokenKiTagAverageAction());

  // 成功または失敗のどちらか早い方を待つ
  const result: { success?: unknown; failure?: unknown } = yield race({
    success: take(DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_SUCCESS),
    failure: take(DataKiSyokenActionTypes.RENEW_DATA_KISYOKEN),
  });

  // 失敗時は処理を中断
  if (result.failure) {
    return;
  }

  const dataKiSyoken = yield* customSelect(dataKiSyokenDataSelector);

  if (!dataKiSyoken) {
    throw new Error('dataKiSyoken is not found');
  }

  // NOTE: 機種集計と同様に、API応答の setting（日付分離後）をUIの検索条件へは反映しない。
  // UIの検索条件はユーザー操作由来（未分離）を保持し、分離は送信時のみ行う。
  // そのため、日付系プロパティはUIへ渡さない。
  // また、containsAverageもタグ選択状態に応じて動的に決定されるため、APIレスポンスの値を保持しない。
  {
    // 既存の検索条件を保持しつつ、ホワイトリストの非日付項目のみ上書き（マージ）
    const currentSearchParams = yield* customSelect(
      kiSyokenSearchParamsSelector
    );
    const {
      ymdList: _ymdList,
      ymdComparisonList: _ymdComparisonList,
      startDate: _startDate,
      endDate: _endDate,
      startComparisonDate: _startComparisonDate,
      endComparisonDate: _endComparisonDate,
      containsAverage: _containsAverage,
      ...restSetting
    } = dataKiSyoken.setting ?? {};

    yield put(
      KiSyokenSettingActionCreators.selectKiSyokenSearchParamsAction({
        ...(currentSearchParams ?? {}),
        ...restSetting,
      })
    );
  }
}

/**
 * 機種リストフィルターのクリア処理を実行するSaga
 */
function* clearKiListFilterSaga() {
  yield put(KiSyokenSettingActionCreators.clearKiSyokenKiListFilterAction());
}

/**
 * 初回データ取得
 */
function* initFetchKiSyokenDataSaga() {
  // 前回の閉じアニメーション中に画面から離脱すると onExited が発火せず、チェーン店一覧の
  // アニメーションフラグが false のまま残る。再訪時に開けなくなるため初期化する
  yield put(
    KiSyokenSettingActionCreators.resetKiSyokenKasidamaAnimationAction()
  );

  const loadingStateKiSyoken = yield* customSelect(
    dataKiSyokenLoadingStateSelector
  );

  if (loadingStateKiSyoken !== 'prepare') {
    return;
  }

  // NOTE: 初期選択の店舗コードは、ユーザー情報または検索条件から取得する
  const user = yield* customSelect(userSelector);

  // ユーザがセットされていない場合は、初回を待機する
  if (!user?.userId) {
    yield take(UserActionTypes.SET_USER);
  }
  const userWithHall = yield* customSelect(userSelector);
  const hallCodeInCharge = userWithHall?.hallCodeInCharge;

  const settingsOptionsLoadingState = yield* customSelect(
    settingsOptionsKiSyokenLoadingStateSelector
  );

  // 検索条件が取得されるまで待機する
  if (
    settingsOptionsLoadingState === 'prepare' ||
    settingsOptionsLoadingState === 'loading'
  ) {
    yield take(
      SettingsOptionsKiSyokenActionTypes.FETCH_SETTINGS_OPTIONS_KISYOKEN_SUCCESS
    );
  }
  let { startDate, startComparisonDate, endDate, endComparisonDate } =
    makeKiSyokenDateFromDateRangeWithoutCustom({
      dateRangeParams: defaultDateRangeParams,
      excludeToday: defaultExcludeToday,
    });

  // 初回フェッチ専用の期間補正: UI更新より先にAPIが発火するため、
  // 主期間の実長に比較期間を合わせ、excludeToday（デフォルトtrue）を適用する
  // 対象: dateRange が『今週』『今月』のときのみ
  const today = new Date();
  const initDateRange = `${defaultDateRangeParams.dateRange}`;
  switch (initDateRange) {
    case '今週': {
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
      const mainStart = weekStart;
      const mainEnd = weekEnd > today ? today : weekEnd;

      const mainDays = eachDayOfInterval({ start: mainStart, end: mainEnd });
      const actualDays = mainDays.length;
      const includesTodayInMain = mainDays
        .map((d) => format(d, 'yyyy-MM-dd'))
        .includes(format(today, 'yyyy-MM-dd'));
      const targetDays =
        defaultExcludeToday && includesTodayInMain && actualDays > 1
          ? actualDays - 1
          : actualDays;

      const prevWeekStart = add(weekStart, { weeks: -1 });
      const prevEnd = add(prevWeekStart, { days: Math.max(0, targetDays - 1) });

      startDate = format(mainStart, 'yyyy-MM-dd');
      endDate = format(mainEnd, 'yyyy-MM-dd');
      startComparisonDate = format(prevWeekStart, 'yyyy-MM-dd');
      endComparisonDate = format(prevEnd, 'yyyy-MM-dd');
      break;
    }
    // 今月は現在デフォルト指定されていないため使われていないが、もしデフォルトを更新した時用に参考として処理を残している
    case '今月': {
      const monthStart = startOfMonth(today);
      const monthEndRaw = endOfMonth(today);
      const mainStart = monthStart;
      const mainEnd = monthEndRaw > today ? today : monthEndRaw;

      const mainDays = eachDayOfInterval({ start: mainStart, end: mainEnd });
      const actualDays = mainDays.length;
      const includesTodayInMain = mainDays
        .map((d) => format(d, 'yyyy-MM-dd'))
        .includes(format(today, 'yyyy-MM-dd'));
      const targetDays =
        defaultExcludeToday && includesTodayInMain && actualDays > 1
          ? actualDays - 1
          : actualDays;

      const lastMonth = add(today, { months: -1 });
      const prevMonthStart = startOfMonth(lastMonth);
      const prevMonthEnd = endOfMonth(lastMonth);
      const prevEndCandidate = add(prevMonthStart, {
        days: Math.max(0, targetDays - 1),
      });
      const prevEnd =
        prevEndCandidate > prevMonthEnd ? prevMonthEnd : prevEndCandidate;

      startDate = format(mainStart, 'yyyy-MM-dd');
      endDate = format(mainEnd, 'yyyy-MM-dd');
      startComparisonDate = format(prevMonthStart, 'yyyy-MM-dd');
      endComparisonDate = format(prevEnd, 'yyyy-MM-dd');
      break;
    }
    default:
      // 今週/今月以外は補正しない
      break;
  }

  const searchCondition = yield* customSelect(
    settingsOptionsKiSyokenSearchConditionSelector
  );

  if (!searchCondition) {
    throw new Error('searchCondition is not found');
  }

  const defaultHallCode =
    hallCodeInCharge && hallCodeInCharge.length > 0
      ? hallCodeInCharge
      : searchCondition.halls.at(0)?.code ?? '';

  // 店舗セレクターに初期値を設定
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenCurrentHallsAction([
      defaultHallCode,
    ])
  );
  // 商圏店舗セレクターに初期値を設定
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenMksHallCodesAction([
      'mksAll',
      ...(searchCondition.halls
        .find((h) => h.code === defaultHallCode)
        ?.hallsOfMarketArea?.map((h) => h.code) ?? []),
    ])
  );

  // UIの検索条件（未分離の値）をフェッチ前に設定してフォームが空にならないようにする
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenDateRangeParamsAction(
      defaultDateRangeParams
    )
  );
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenSearchParamsAction({
      halls: [defaultHallCode],
      startDate: startDate,
      endDate: endDate,
      startComparisonDate: startComparisonDate,
      endComparisonDate: endComparisonDate,
    })
  );

  const defaultShuGroupList =
    searchCondition.shuGroupList.at(0)?.id ?? undefined;

  // 初期選択の店舗・期間・種別でデータを取得する
  // 統一フロー: 直接fetchせず、検索アクション経由で送信直前の分離/長さ調整/当日除外ロジックを適用
  yield put(
    KiSyokenSettingActionCreators.searchKiSyokenSettingsAction({
      params: {
        halls: [defaultHallCode],
        startDate,
        endDate,
        startComparisonDate,
        endComparisonDate,
        excludeToday: defaultExcludeToday,
        shuGroupIds: defaultShuGroupList,
      } as KiSyokenSearchParams,
      dateRangeParams: defaultDateRangeParams,
      selectedKiList: [],
    })
  );
}

function* triggerKiSyokenSwapFieldsSaga(
  action: TriggerKiSyokenSwapFieldsAction
) {
  const { draggedId, droppedId } = action.payload;

  const KiSyokenData = yield* customSelect(dataKiSyokenDataSelector);
  if (KiSyokenData == null) {
    return;
  }

  const currentOrder = yield* customSelect(kiSyokenColumnsOrderSelector);

  const ordered = recalcColumnOrder(
    currentOrder.length === 0
      ? KiSyokenData.data.columns
      : currentOrder.map((code) => ({ code })),
    draggedId,
    droppedId
  );

  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenColumnsOrderAction(ordered)
  );
}

/**
 * タグに該当する機種リストを取得する共通関数
 * @param selectedKiTags 選択されているタグ一覧
 * @returns resultKiList（全タグに該当する機種）とfilteredKiListByTags（タグごとの機種）
 */
function* getFilteredKiListsByTags(selectedKiTags: KiTagListOptions[]) {
  // メインAPIの生データを参照する（dataKiSyokenResultSelector は不可）。
  // PPM区分などクライアントのみのフィルターで行が絞られると、タグに紐づく機種が
  // 画面上非表示でもレスポンスには含まれるため、resultKiList が空になり
  // タグ平均行の再取得がスキップされてしまう。
  const dataKiSyoken = yield* customSelect(dataKiSyokenDataSelector);
  if (!dataKiSyoken) {
    return null;
  }

  // 機種別商圏のsearchConditionの検索条件を取得
  // 機種タグ設定画面で登録された機種タグ一覧を抽出するため
  const kiTagList = yield* customSelect(
    settingsOptionsKiSyokenKiTagListSelector
  );
  if (!kiTagList) {
    return null;
  }

  // 選択されたタグに紐づく機種とdata/kiSyokenで設定されている機種の比較と抽出
  const mergedKiLists = retrieveMatchedIdLists(kiTagList, selectedKiTags);

  // データに存在する機種のみをリクエストする
  // 不要なkiListを送信するとURLが長くなり414エラーとなるため
  const dataKiList = dataKiSyoken.data.rows
    .filter((item) => item.queryParameter.name === 'kiList')
    .map((item) => item.queryParameter.value)
    .filter((value): value is string => value != null);

  const resultKiList = dataKiList.filter((item) =>
    mergedKiLists.includes(item)
  );

  // 以下はタグ別平均行用
  // selectedKiTags毎にretrieveMatchedIdListsを実行して、配列で管理する
  const kiListByTags = selectedKiTags.map((item) => {
    return kiTagList ? retrieveMatchedIdLists(kiTagList, [item]) : [];
  });

  const filteredKiListByTags = kiListByTags.map((item) => {
    return dataKiList.filter((dataItem) => item.includes(dataItem));
  });

  return { resultKiList, filteredKiListByTags };
}

/**
 * タグ別平均合計行データ取得（期間再計算付き）
 */
function* searchActionKiTagAverageSaga(action: SearchActionKiTagAverageAction) {
  const { params, selectedKiTags, previousKiTags } = action.payload;

  // 現在の検索条件と期間設定を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );

  // 検索条件を構築
  const searchCondition: KiSyokenSearchParams = {
    ...searchParams,
    ...params,
  };

  // 共通の日付処理ロジックを使用
  const finalParams = yield* processDateParamsForApiSaga(
    searchCondition,
    selectedDateRangeParams
  );

  // タグ別平均合計を取得する際は機種リスト平均合計をクリア
  yield put(DataKiSyokenActionCreators.resetDataKiSyokenKiListAverageAction());

  // タグ絞り込み平均合計行を取得
  if (selectedKiTags.length > 0) {
    // タグに該当する機種リストを取得
    const filteredData = yield* getFilteredKiListsByTags(selectedKiTags);
    if (!filteredData) {
      return;
    }

    const { resultKiList, filteredKiListByTags } = filteredData;

    // タグの変更を検出
    const hasTagChanges =
      selectedKiTags.length !== previousKiTags.length ||
      !selectedKiTags.every((tag) =>
        previousKiTags.some((prevTag) => prevTag.tagId === tag.tagId)
      );

    // 機種タグ絞り込みで機種行が、タグ選択などで変更されたとき
    // 機種タグに紐づいた機種の平均合計行を取得する
    if (hasTagChanges && resultKiList.length > 0) {
      yield put(
        DataKiSyokenActionCreators.fetchDataKiSyokenFilteredKiTagAverageAction(
          finalParams,
          selectedKiTags
        )
      );
    } else if (hasTagChanges && resultKiList.length === 0) {
      // 機種が0件の場合は平均合計行データをクリア
      yield put(
        DataKiSyokenActionCreators.clearDataKiSyokenFilteredTagAverageAction()
      );
    }

    // 新しいタグが追加された場合のみ、新しいタグの平均合計を取得
    if (selectedKiTags.length > previousKiTags.length) {
      for (let i = 0; i < filteredKiListByTags.length; i++) {
        const tag = selectedKiTags.at(i);
        const tagKiList = filteredKiListByTags.at(i);
        // 新しく追加されたタグかどうかをチェック
        const isNewTag =
          tag?.tagId != null &&
          !previousKiTags.some((prevTag) => prevTag.tagId === tag.tagId);
        if (isNewTag && tagKiList && tagKiList.length > 0) {
          yield put(
            DataKiSyokenActionCreators.fetchDataKiSyokenKiTagAverageAction(
              finalParams,
              tag.tagId.toString()
            )
          );
        }
      }
    }
  }
}

/**
 * タグ別平均合計行データ取得（検索条件変更時）
 */
function* searchActionKiTagAverageOnSearchConditionChangeSaga(
  action: SearchActionKiTagAverageOnSearchConditionChangeAction
) {
  const { params, selectedKiTags } = action.payload;

  // メインデータが取得済みでない場合は完了を待つ
  const loadingState = yield* customSelect(dataKiSyokenLoadingStateSelector);
  if (loadingState !== 'loaded') {
    // 成功または失敗のどちらか早い方を待つ
    yield race({
      success: take(DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_SUCCESS),
      failure: take(DataKiSyokenActionTypes.RENEW_DATA_KISYOKEN),
    });
  }

  // 現在の検索条件と期間設定を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );

  // 検索条件を構築
  const searchCondition: KiSyokenSearchParams = {
    ...searchParams,
    ...params,
  };

  // 共通の日付処理ロジックを使用
  const finalParams = yield* processDateParamsForApiSaga(
    searchCondition,
    selectedDateRangeParams
  );

  // タグ別平均合計を取得する際は機種リスト平均合計をクリア
  yield put(DataKiSyokenActionCreators.resetDataKiSyokenKiListAverageAction());

  // 検索条件変更時は全てのタグの平均合計を再取得
  if (selectedKiTags.length > 0) {
    // 前回のタグの平均合計行データをクリア
    yield put(
      DataKiSyokenActionCreators.clearDataKiSyokenFilteredTagAverageAction()
    );
    yield put(DataKiSyokenActionCreators.clearDataKiSyokenKiTagAverageAction());

    // タグに該当する機種リストを取得
    const filteredData = yield* getFilteredKiListsByTags(selectedKiTags);
    if (!filteredData) {
      return;
    }

    const { resultKiList, filteredKiListByTags } = filteredData;

    // 機種タグ絞り込みで機種行が、検索条件変更などで変更されたとき
    // 機種タグに紐づいた機種の平均合計行を取得する
    if (resultKiList.length > 0) {
      yield put(
        DataKiSyokenActionCreators.fetchDataKiSyokenFilteredKiTagAverageAction(
          finalParams,
          selectedKiTags
        )
      );
      for (let i = 0; i < filteredKiListByTags.length; i++) {
        const tag = selectedKiTags.at(i);
        const tagKiList = filteredKiListByTags.at(i);
        if (tag?.tagId != null && tagKiList && tagKiList.length > 0) {
          yield put(
            DataKiSyokenActionCreators.fetchDataKiSyokenKiTagAverageAction(
              finalParams,
              tag.tagId.toString()
            )
          );
        }
      }
    }
  }
}

/**
 * 平均合計行データ取得（期間再計算付き）
 */
function* searchActionKiListAverageSaga(
  action: SearchActionKiListAverageAction
) {
  const { params, selectedKiList } = action.payload;

  yield put(
    DataKiSyokenKasidamaActionCreators.renewDataKiSyokenKasidamaAction()
  );

  // メインデータが取得済みでない場合は完了を待つ
  const loadingState = yield* customSelect(dataKiSyokenLoadingStateSelector);
  if (loadingState !== 'loaded') {
    // 成功または失敗のどちらか早い方を待つ
    yield race({
      success: take(DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_SUCCESS),
      failure: take(DataKiSyokenActionTypes.RENEW_DATA_KISYOKEN),
    });
  }

  // 現在の検索条件と期間設定を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );

  // 検索条件を構築
  const searchCondition: KiSyokenSearchParams = {
    ...searchParams,
    ...params,
  };

  // 共通の日付処理ロジックを使用
  const finalParams = yield* processDateParamsForApiSaga(
    searchCondition,
    selectedDateRangeParams
  );

  // 機種リスト平均合計を取得する際はタグ別平均合計をクリア
  yield put(
    DataKiSyokenActionCreators.clearDataKiSyokenFilteredTagAverageAction()
  );
  yield put(DataKiSyokenActionCreators.clearDataKiSyokenKiTagAverageAction());

  // 機種リスト平均合計を取得する際はタグ絞り込みを解除
  yield put(KiSyokenSettingActionCreators.selectKiSyokenKiTagListAction([]));

  // 平均合計行データを取得
  yield put(
    DataKiSyokenActionCreators.fetchDataKiSyokenKiListAverageAction(
      finalParams,
      selectedKiList
    )
  );
}

/**
 * 検索ボタンクリック
 */
function* searchActionSaga(action: SearchKiSyokenSettingsAction) {
  const {
    params,
    dateRangeParams,
    selectedKiList,
    skipTagAverageRequest = false,
  } = action.payload;

  // 選択中の種別・種別グループを取得する
  const currentShu = yield* customSelect(kiSyokenCurrentShuSelector);

  // 現在の検索条件を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);

  const searchCondition: KiSyokenSearchParams = {
    kiList: selectedKiList,
    kiListForSijiritu: selectedKiList,
    makersForSijiritu: params.makers,
    sisTypesForSijiritu: params.sisTypes,
    ...{ ...searchParams, ...params },
  };

  // 共通の日付処理ロジックを使用
  const modifyParams = yield* processDateParamsForApiSaga(
    searchCondition,
    dateRangeParams
  );

  // 商圏セレクタの選択状態の変更（元のsearchConditionを使用）
  yield fork(selectKiSyokenMksHallCodesActionSaga, searchCondition);

  // 期間をStoreに格納する
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenDateRangeParamsAction(
      dateRangeParams
    )
  );
  // 機種集計同様、検索時はフォームのローカル状態を維持するため、ここではUI検索条件を上書きしない

  // 分離済みパラメータでデータ取得
  // 注意: paramsに既に種別が設定されている場合（種別セレクター経由）は、currentShu で上書きしない
  const finalShuCondition = resolveKiSyokenShuCondition(params, currentShu);
  const resolvedSearchParams = buildKiSyokenResolvedSearchParams(
    modifyParams,
    finalShuCondition
  );
  yield fork(fetchDataKiSyokenSaga, resolvedSearchParams);

  // メインデータの取得完了を待つ（タグ平均行取得で正しいデータを参照するため）
  // 成功または失敗のどちらか早い方を待つ
  yield race({
    success: take(DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_SUCCESS),
    failure: take(DataKiSyokenActionTypes.RENEW_DATA_KISYOKEN),
  });

  // 検索条件変更時にタグが選択されている場合は、タグ別平均合計行も再取得
  // skipTagAverageRequestがtrueの場合はスキップ（お気に入り適用時など）
  if (!skipTagAverageRequest) {
    const selectedKiTags = yield* customSelect(kiSyokenTagListSelector);
    if (selectedKiTags && selectedKiTags.length > 0) {
      // タグ平均もメイン検索と同じ種別条件（finalShuCondition）を渡す。
      // 種別が欠落すると稼動率がずれる（claris-general#6200）。
      // タグ平均 Saga 側で日付は再処理するため、日付処理前の params を基点にする。
      yield put(
        searchActionKiTagAverageOnSearchConditionChangeAction(
          buildKiSyokenResolvedSearchParams(params, finalShuCondition),
          selectedKiTags
        )
      );
    }
  }
}

/**
 * 商圏セレクタの変更処理
 */
function* selectKiSyokenMksHallCodesActionSaga(
  searchParams: KiSyokenSearchParams
) {
  const dataKiSyoken = yield* customSelect(dataKiSyokenDataSelector);
  // 検索した店舗が前回の店舗と異なるかどうかをチェック
  const isDifferentHall =
    dataKiSyoken?.setting?.halls?.[0] !== searchParams.halls?.[0];

  // 商圏セレクタの選択状態の変更(同一店舗の場合は処理スキップ)
  if (isDifferentHall) {
    const searchCondition = yield* customSelect(
      settingsOptionsKiSyokenSearchConditionSelector
    );

    if (!searchCondition) {
      throw new Error('searchCondition is not found');
    }

    // 商圏店舗が表示されるようにする
    const targetHall = searchCondition.halls.find(
      (h) => h.code === searchParams.halls?.[0]
    );

    yield put(
      KiSyokenSettingActionCreators.selectKiSyokenMksHallCodesAction([
        'mksAll',
        ...(targetHall?.hallsOfMarketArea?.map((h) => h.code) ?? []),
      ])
    );
  }

  return;
}

/**
 * 機種別商圏のリセットボタン押下
 */
function* resetSearchActionSaga() {
  const favoriteId = yield* customSelect(kiSyokenSelectedFavoriteSelector);

  // お気に入りが選択されていない場合はデフォルト値でリクエストする
  if (favoriteId == null) {
    yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());
    yield put(
      DataKiSyokenActionCreators.resetDataKiSyokenKiListAverageAction()
    );
    yield fork(clearAllDataKiSyokenSaga);
    yield fork(initFetchKiSyokenDataSaga);
    return;
  }

  yield put(
    KiSyokenSettingActionCreators.changeKiSyokenFavoriteAction(favoriteId)
  );
}

/**
 * 変更後の店舗でデータを取得する
 */
function* changeCurrentHallsSaga(
  action: ReturnType<
    typeof KiSyokenSettingActionCreators.changeKiSyokenCurrentHallsAction
  >
) {
  // 店舗が選択されていない場合は処理を中断
  if (action.payload.halls === undefined) {
    return;
  }

  // 現在の検索条件を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);

  yield fork(clearKiListFilterSaga);

  // 商圏セレクタの選択状態の変更
  yield fork(selectKiSyokenMksHallCodesActionSaga, searchParams);

  // 変更後の店舗でデータを取得する（統一フローを再利用）
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );

  // メインテーブルのリクエストにはcontainsAverageを設定しない
  // タグ平均合計行は searchActionSaga 内の searchActionKiTagAverageOnSearchConditionChangeSaga で別途取得される
  yield put(
    KiSyokenSettingActionCreators.searchKiSyokenSettingsAction({
      params: {
        ...searchParams,
        halls: action.payload.halls,
      },
      dateRangeParams: selectedDateRangeParams,
      selectedKiList: searchParams?.kiList ?? [],
    })
  );
}

/**
 * 変更後の種別でデータを取得する
 */
function* changeCurrentShuSaga(
  action: ReturnType<
    typeof KiSyokenSettingActionCreators.changeKiSyokenCurrentShuAction
  >
) {
  // 種別が選択されていない場合は処理を中断
  if (action.payload.shu === undefined) return;

  // 現在の検索条件を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);

  yield fork(clearKiListFilterSaga);

  // 変更後の種別でデータを取得する（統一フローを再利用）
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );
  const shuCondition = selectShu2SearchCondition(action.payload.shu);

  // メインテーブルのリクエストにはcontainsAverageを設定しない
  // タグ平均合計行は searchActionSaga 内の searchActionKiTagAverageOnSearchConditionChangeSaga で別途取得される
  const paramsWithShu = {
    ...searchParams,
    ...shuCondition,
  };

  yield put(
    KiSyokenSettingActionCreators.searchKiSyokenSettingsAction({
      params: paramsWithShu,
      dateRangeParams: selectedDateRangeParams,
      selectedKiList: paramsWithShu?.kiList ?? [],
    })
  );
}

/**
 * 表示項目変更時のリクエスト
 */
function* changeKiSyokenFieldsSaga(action: ChangeKiSyokenFieldsAction) {
  const { notSyokenFields, isSyokenFields, fieldsOrder } = action.payload;

  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);

  // 表示項目パターン適用時は、保存済み順序（fieldsOrder）をそのまま優先して適用する
  // 通常の手動選択時のみ、商圏/非商圏の選択状態から結合順を作る
  const combinedFields =
    fieldsOrder && fieldsOrder.length > 0
      ? fieldsOrder
      : // notSyokenFieldsとisSyokenFieldsを一つのオブジェクトにまとめる
        [...notSyokenFields, ...isSyokenFields];

  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenFieldsAction(combinedFields)
  );

  // 表示項目変更時も比較区分の分離ロジックを通すため、検索フローを再利用
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );
  yield put(
    KiSyokenSettingActionCreators.searchKiSyokenSettingsAction({
      params: { ...searchParams, fields: combinedFields },
      dateRangeParams: selectedDateRangeParams,
      selectedKiList: searchParams?.kiList ?? [],
    })
  );

  // 機種絞り込みが有効な場合、平均合計行を再取得
  const kiListFilter = yield* customSelect(kiSyokenListFilterSelector);
  if (kiListFilter && kiListFilter.length > 0) {
    yield put(
      KiSyokenSettingActionCreators.searchActionKiListAverageAction(
        { ...searchParams, fields: combinedFields },
        kiListFilter
      )
    );
  }

  // 以下：新規追加された表示項目は一律前に追加処理
  const dataSyoken = yield* customSelect(dataColumnsSwappedSelector);

  const allFields = yield* customSelect(settingsOptionsKiSyokenFieldsSelector);
  if (dataSyoken == null) {
    throw new Error('dataSyoken is not found');
  }
  if (allFields == null) {
    throw new Error('settingsOptions is not found');
  }

  const columnsOrder = dataSyoken.data.columns
    .map((column) => column.code)
    .filter((code) => code != null) as string[];

  const resultOrder = buildColumnsOrder(
    fieldsOrder,
    combinedFields,
    columnsOrder,
    allFields.ki
  );

  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenColumnsOrderAction(resultOrder)
  );
}

// 期間スライドコンポーネントがクリックされた時に検索期間を設定して再検索を実行する
function* searchDateRangeSlideSaga(
  action: ReturnType<
    typeof KiSyokenSettingActionCreators.searchKiSyokenDateRangeSlideAction
  >
) {
  const {
    dateRange,
    startDate,
    endDate,
    startComparisonDate,
    endComparisonDate,
  } = action.payload;

  // 現在の検索条件を取得
  const searchParams = yield* customSelect(kiSyokenSearchParamsSelector);

  // 現在の期間・比較区分を取得
  const selectedDateRangeParams = yield* customSelect(
    kiSyokenSelectedDateRangeParamsSelector
  );
  const comparisonSection = selectedDateRangeParams.comparisonSection;
  const excludeToday = !!searchParams?.excludeToday;

  // カスタム日数を取得（過去xx日の設定値）
  const customDate = yield* customSelect(kiSyokenCustomDateSelector);

  // スライドした期間に合わせて期間計算を実行
  let calculatedStartDate = startDate;
  let calculatedEndDate = endDate;
  let calculatedStartComparisonDate = startComparisonDate;
  let calculatedEndComparisonDate = endComparisonDate;

  if (dateRange === '今週') {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const mainStart = weekStart;
    const mainEnd = weekEnd > today ? today : weekEnd;

    const mainDays = eachDayOfInterval({ start: mainStart, end: mainEnd });
    const actualDays = mainDays.length;
    const includesTodayInMain = mainDays
      .map((d) => format(d, 'yyyy-MM-dd'))
      .includes(format(today, 'yyyy-MM-dd'));
    const targetDays =
      excludeToday && includesTodayInMain && actualDays > 1
        ? actualDays - 1
        : actualDays;

    const prevWeekStart = add(weekStart, { weeks: -1 });
    const prevEnd = add(prevWeekStart, { days: Math.max(0, targetDays - 1) });

    calculatedStartDate = format(mainStart, 'yyyy-MM-dd');
    calculatedEndDate = format(mainEnd, 'yyyy-MM-dd');
    calculatedStartComparisonDate = format(prevWeekStart, 'yyyy-MM-dd');
    calculatedEndComparisonDate = format(prevEnd, 'yyyy-MM-dd');
  } else if (dateRange === '前週') {
    const today = new Date();
    const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    const lastWeekStart = add(thisWeekStart, { weeks: -1 });
    const lastWeekEnd = add(lastWeekStart, { days: 6 });

    // 前週の期間を計算
    calculatedStartDate = format(lastWeekStart, 'yyyy-MM-dd');
    calculatedEndDate = format(lastWeekEnd, 'yyyy-MM-dd');

    // 比較期間は前週の前の週（前前週）
    const prevPrevWeekStart = add(lastWeekStart, { weeks: -1 });
    const prevPrevWeekEnd = add(prevPrevWeekStart, { days: 6 });

    calculatedStartComparisonDate = format(prevPrevWeekStart, 'yyyy-MM-dd');
    calculatedEndComparisonDate = format(prevPrevWeekEnd, 'yyyy-MM-dd');
  } else if (dateRange === '前日') {
    const today = new Date();
    const yesterday = add(today, { days: -1 });

    // 前日の期間を計算
    calculatedStartDate = format(yesterday, 'yyyy-MM-dd');
    calculatedEndDate = format(yesterday, 'yyyy-MM-dd');

    // カスタム日数を使用して比較期間を計算（過去xx日を考慮）
    const originalPeriodDays = customDate ? parseInt(customDate, 10) : 0;

    // 比較期間は元の検索期間の日数分だけ過去にさかのぼる
    if (originalPeriodDays > 0 && !isNaN(originalPeriodDays)) {
      // 過去xx日を考慮した比較期間の計算
      const comparisonDate = add(yesterday, { days: -originalPeriodDays });
      calculatedStartComparisonDate = format(comparisonDate, 'yyyy-MM-dd');
      calculatedEndComparisonDate = format(comparisonDate, 'yyyy-MM-dd');
    } else {
      // フォールバック：従来の比較区分に応じた設定
      if (comparisonSection === '前日') {
        // 前日の前日（一昨日）
        const dayBeforeYesterday = add(yesterday, { days: -1 });
        calculatedStartComparisonDate = format(
          dayBeforeYesterday,
          'yyyy-MM-dd'
        );
        calculatedEndComparisonDate = format(dayBeforeYesterday, 'yyyy-MM-dd');
      } else {
        // 前週同曜日（デフォルト）
        const weekAgo = add(yesterday, { days: -7 });
        calculatedStartComparisonDate = format(weekAgo, 'yyyy-MM-dd');
        calculatedEndComparisonDate = format(weekAgo, 'yyyy-MM-dd');
      }
    }
  }

  // UI表示用：スライダーの指定からymdListを生成
  const makeList = (startDate?: string, endDate?: string) =>
    startDate && endDate
      ? eachDayOfInterval({
          start: new Date(startDate),
          end: new Date(endDate),
        }).map((d) => format(d, 'yyyy-MM-dd'))
      : undefined;
  const rawYmdList = makeList(calculatedStartDate, calculatedEndDate) ?? [];
  let ymdList = [...rawYmdList];

  // フォールバック：ymdListが空になった場合でも必ず保持
  if (ymdList.length === 0 && calculatedStartDate) {
    const fallback = makeList(calculatedStartDate, calculatedEndDate);
    ymdList =
      fallback && fallback.length > 0 ? fallback : [calculatedStartDate];
  }

  // 比較期間の再計算：useDateと同じロジックを使用
  let ymdComparisonList: string[] = [];

  // 今週、前週、前日の場合は既に比較期間が計算済みなので、再計算をスキップ
  if (dateRange === '今週' || dateRange === '前週' || dateRange === '前日') {
    ymdComparisonList =
      makeList(calculatedStartComparisonDate, calculatedEndComparisonDate) ??
      [];
  } else {
    // 比較期間の再計算が必要かチェック
    const needsRecalculation =
      isComparisonPeriodRecalculationNeeded(comparisonSection);

    if (needsRecalculation && calculatedStartDate && calculatedEndDate) {
      // 月単位の前月/前年同月は専用計算
      if (
        dateRange === '今月' &&
        (comparisonSection === '前月' || comparisonSection === '前年同月')
      ) {
        ymdComparisonList = calculateMonthComparisonPeriodInUseDate(
          comparisonSection,
          new Date(calculatedStartDate),
          new Date(calculatedEndDate),
          excludeToday
        );
        calculatedStartComparisonDate = ymdComparisonList[0];
        calculatedEndComparisonDate =
          ymdComparisonList[ymdComparisonList.length - 1];
      } else {
        // その他の比較区分：統一された計算ロジックを使用
        const comparisonResult = calculateComparisonPeriod(
          comparisonSection,
          calculatedStartDate,
          calculatedEndDate,
          excludeToday
        );
        calculatedStartComparisonDate = comparisonResult.startComparisonDate;
        calculatedEndComparisonDate = comparisonResult.endComparisonDate;
        ymdComparisonList = comparisonResult.ymdComparisonList ?? [];
      }
    } else {
      // 再計算不要の場合：スライダーの値をそのまま使用
      const rawYmdComparisonList =
        makeList(calculatedStartComparisonDate, calculatedEndComparisonDate) ??
        [];
      ymdComparisonList = [...rawYmdComparisonList];

      // 平日/土日祝比較の場合は未分離の同一配列を保持
      if (comparisonSection === '平日/土日祝比較') {
        ymdComparisonList = [...ymdList];
      }

      // フォールバック処理
      if (ymdComparisonList.length === 0) {
        if (calculatedStartComparisonDate) {
          const fallback = makeList(
            calculatedStartComparisonDate,
            calculatedEndComparisonDate
          );
          ymdComparisonList =
            fallback && fallback.length > 0
              ? fallback
              : [calculatedStartComparisonDate];
        } else if (comparisonSection === '平日/土日祝比較') {
          ymdComparisonList = [...ymdList];
        }
      }
    }
  }

  // 期間（dateRange）のみ更新（機種集計と同様にUIの選択状態を維持）
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenDateRangeParamsAction({
      ...selectedDateRangeParams,
      dateRange,
    })
  );

  // フェッチ前にUIへ反映（計算された比較期間を使用）
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenSearchParamsAction({
      ...searchParams,
      startDate: calculatedStartDate,
      endDate: calculatedEndDate,
      startComparisonDate: calculatedStartComparisonDate,
      endComparisonDate: calculatedEndComparisonDate,
      ymdList,
      ymdComparisonList,
    })
  );

  yield put(
    KiSyokenSettingActionCreators.searchKiSyokenSettingsAction({
      params: {
        ...searchParams,
        startDate: calculatedStartDate,
        endDate: calculatedEndDate,
        startComparisonDate: calculatedStartComparisonDate,
        endComparisonDate: calculatedEndComparisonDate,
        ymdList,
        ymdComparisonList,
        excludeToday,
      },
      dateRangeParams: {
        ...selectedDateRangeParams,
        dateRange,
      },
      selectedKiList: searchParams?.kiList ?? [],
    })
  );
}

// 検索ボタンクリック時にデータを取得する
function* handleSearchSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_KI_SYOKEN_SETTINGS,
    searchActionSaga
  );
}

/**
 * 初回表示時のデータ取得
 */
function* handleInitFetchSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.INIT_KISYOKEN_SETTING,
    initFetchKiSyokenDataSaga
  );
}
/**
 * お気に入り選択中または未選択時のデフォルト値にする
 */
function* handleSearchResetSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_RESET_KI_SYOKEN_SEARCH_PARAMS,
    resetSearchActionSaga
  );
}

/**
 * 表示項目変更時のデータ取得
 */
function* handleChangeFieldSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_FIELDS,
    changeKiSyokenFieldsSaga
  );
}

/**
 * 表示項目並び順の変更
 */
function* handleFieldOrderSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.TRIGGER_KISYOKEN_SWAP_FIELDS,
    triggerKiSyokenSwapFieldsSaga
  );
}

/**
 * 選択中の店舗変更時、データを取得する
 */
function* handleChangeCurrentHallsSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_CURRENT_HALLS,
    changeCurrentHallsSaga
  );
}

/**
 * 選択中の種別変更時、データを取得する
 */
function* handleChangeCurrentShuSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_CURRENT_SHU,
    changeCurrentShuSaga
  );
}

/**
 * 期間スライドコンポーネントがクリックされた時に実行する処理
 */
function* handleDateRangeSlideSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_KISYOKEN_DATE_RANGE_SLIDE,
    searchDateRangeSlideSaga
  );
}

/**
 * Settingsとチェーン店のデータを破棄
 */
function* clearAllDataKiSyokenSaga() {
  yield put(KiSyokenSettingActionCreators.renewKiSyokenSettingAction());
  yield put(
    DataKiSyokenKasidamaActionCreators.renewDataKiSyokenKasidamaAction()
  );
}

/**
 * お気に入り選択時、検索条件に反映する
 * @param action Action
 */
function* applyFavoriteByIdSaga(action: ChangeKiSyokenFavoriteAction) {
  const favoriteItem = yield* customSelect(
    kiSyokenSelectedFavoriteDataSelector
  );

  // 選択されたお気に入りID
  const selectedFavoriteId = action.payload.favoriteId;

  // デフォルトのお気に入りを選択した場合、初回取得と同様の動作を行う
  if (selectedFavoriteId === undefined || favoriteItem === undefined) {
    yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());
    yield fork(clearAllDataKiSyokenSaga);
    yield put(KiSyokenSettingActionCreators.initKiSyokenSettingAction());
    return;
  }

  /** お気に入り選択時、チェーン店の情報を初期化する */
  yield put(
    DataKiSyokenKasidamaActionCreators.renewDataKiSyokenKasidamaAction()
  );

  /** お気に入り適用時にタグの平均合計行をクリア */
  yield put(
    DataKiSyokenActionCreators.clearDataKiSyokenFilteredTagAverageAction()
  );
  yield put(DataKiSyokenActionCreators.clearDataKiSyokenKiTagAverageAction());

  /** 現在選択中のお気に入りデータを取得 */
  const favorite = yield* customSelect(
    kiSyokenSelectedFavoritePageSettingSelector
  );
  if (favorite == null) {
    throw new Error('該当するお気に入りが見つかりません');
  }

  const lastUpdateAt = favoriteItem.updatedAt ?? favoriteItem.createdAt;

  if (lastUpdateAt == null) {
    throw new Error('お気に入りの作成日が存在しません');
  }

  yield fork(applyKiSyokenFavoriteSaga, favorite);
}

export const kiSyokenMultiFilterFromFavorite = multiFilterFromFavoriteWithType;

/** Favoriteの内容を実際に反映させる
 *
 * Why（制限共有グループと sanitize の順序）:
 * - `sanitizeKiSyokenFavoriteForRestrictedKyoyu` は `fields.ki` で商圏のみ保存かどうかを判定するため、
 *   `settingsOptionsKiSyoken` を取得してから呼ぶ必要がある（未取得のままだと商圏のみ→デフォルト表示への補正がスキップされる）。
 */
export function* applyKiSyokenFavoriteSaga(rawFavorite: KiSyokenFavorite) {
  const user = yield* ensureUserLoadedForSaga();
  const loadingState = yield* customSelect(dataKiSyokenLoadingStateSelector);

  // 進行中のfetchがある場合は完了を待つ
  if (loadingState === 'loading') {
    yield race({
      success: take(DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_SUCCESS),
      failure: take(DataKiSyokenActionTypes.RENEW_DATA_KISYOKEN),
    });
  }

  const settingsOptionsKiSyokenLoadingState = yield* customSelect(
    settingsOptionsKiSyokenLoadingStateSelector
  );

  /** 画面リンクなどから直接アクセスされた場合、settingsOptionsが取得されていない場合があるため、取得する */
  if (settingsOptionsKiSyokenLoadingState === 'prepare') {
    yield put(
      SettingsOptionsKiSyokenActionCreators.fetchSettingsOptionsKiSyokenAction()
    );

    yield take(
      SettingsOptionsKiSyokenActionTypes.FETCH_SETTINGS_OPTIONS_KISYOKEN_SUCCESS
    );
  }

  // 制限共有グループ向けのお気に入り補正に、表示項目マスタ（isSyoken 判定）が必要なため options 取得後に sanitize する
  const kiSyokenFieldOptions = yield* customSelect(
    settingsOptionsKiSyokenFieldsSelector
  );
  const favorite = sanitizeKiSyokenFavoriteForRestrictedKyoyu(
    rawFavorite,
    user?.kyoyuGroupCode,
    kiSyokenFieldOptions?.ki
  );

  // お気に入り適用前に既存データをクリアして、次のfetchを明確にする
  yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());

  // race condition回避: デフォルトfetchのsearchActionSagaが、お気に入りの設定を参照しないよう、一旦クリア
  yield put(KiSyokenSettingActionCreators.selectKiSyokenKiTagListAction([]));
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenSelectKiListFilterAction([])
  );

  const convertList = ['過去7日', '過去14日', '過去28日'] as const;
  type ConvertDateRange = (typeof convertList)[number];

  // 期間を適用する
  const originDateRange = favorite.selectedDateRangeParams.dateRange;

  // dateUnit実装前のお気に入りを考慮
  const dateUnit =
    favorite.selectedDateRangeParams.dateUnit ??
    defaultDateRangeParams.dateUnit;
  // 比較区分実装前のお気に入りを考慮
  const comparisonSection =
    favorite.selectedDateRangeParams.comparisonSection ??
    (convertList.includes(originDateRange as ConvertDateRange)
      ? '過去xx日'
      : filteredKiSyokenDateRanges.find(({ name }) => name === originDateRange)
          ?.defaultComparisonSection ?? 'カスタム');

  // dateRangeの変換を行う
  type ConvertTarget = (typeof convertList)[number];
  function isConvertTarget(value: string): value is ConvertTarget {
    return convertList.includes(value as ConvertTarget);
  }
  function convertDateRange(
    originDateRange: string
  ): FilteredKiSyokenDateRange {
    return isConvertTarget(originDateRange)
      ? '過去xx日'
      : (originDateRange as FilteredKiSyokenDateRange);
  }
  function convertComparisonDateRange(
    originComparativeSection: string
  ): FilteredKiSyokenComparisonSection {
    return isConvertTarget(originComparativeSection)
      ? '過去xx日'
      : (originComparativeSection as FilteredKiSyokenComparisonSection);
  }

  // 期間設定を変換して反映
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenDateRangeParamsAction({
      dateRange: convertDateRange(originDateRange),
      dateUnit,
      comparisonSection: convertComparisonDateRange(comparisonSection),
    })
  );

  /** 過去xx日が実装される前に作成されたお気に入りを考慮した処理 */
  // 過去xx日が実装される前に作成されたお気に入りは、テンプレートが過去7日/過去14日/過去28日のいずれかで保存されているため、
  // customDateに変換して後続の期間計算ロジックに渡す必要がある
  let customDate = favorite.customDate;
  if (originDateRange === '過去7日') {
    customDate = '7';
  } else if (originDateRange === '過去14日') {
    customDate = '14';
  } else if (originDateRange === '過去28日') {
    customDate = '28';
  }

  /** 検索条件の期間部分を再計算 */
  // カスタム期間は保存された日付をそのまま使用し、カスタム期間以外は復元時に復元当日を起点に再計算する
  const calcDateRange = (() => {
    const dateRange = convertDateRange(originDateRange);
    const comparisonSectionConverted =
      convertComparisonDateRange(comparisonSection);

    // 自由選択は ymdList / ymdComparisonList をそのまま使用
    if (dateUnit === '自由選択') {
      return {
        ...(favorite.searchParams.ymdList != null && {
          ymdList: favorite.searchParams.ymdList,
        }),
        ...(favorite.searchParams.ymdComparisonList != null && {
          ymdComparisonList: favorite.searchParams.ymdComparisonList,
        }),
      };
    }

    // カスタム期間の場合のみ、保存された期間情報をそのまま使用
    if (dateRange === 'カスタム') {
      if (
        favorite.searchParams.startDate &&
        favorite.searchParams.endDate &&
        favorite.searchParams.startComparisonDate &&
        favorite.searchParams.endComparisonDate
      ) {
        return {
          startDate: favorite.searchParams.startDate,
          endDate: favorite.searchParams.endDate,
          startComparisonDate: favorite.searchParams.startComparisonDate,
          endComparisonDate: favorite.searchParams.endComparisonDate,
          // ymdListとymdComparisonListも保存されている場合は使用
          ...(favorite.searchParams.ymdList && {
            ymdList: favorite.searchParams.ymdList,
          }),
          ...(favorite.searchParams.ymdComparisonList && {
            ymdComparisonList: favorite.searchParams.ymdComparisonList,
          }),
        };
      }
    }

    // カスタム期間以外（今週、前週、今月等の相対期間）は常に再計算
    // 復元時の現在日付を基準に期間を計算し直す
    return makeKiSyokenDateFromDateRange({
      dateRangeParams: {
        ...favorite.selectedDateRangeParams,
        dateRange: dateRange,
        comparisonSection: comparisonSectionConverted,
      },
      searchParams: favorite.searchParams,
      excludeToday: favorite.searchParams.excludeToday ?? defaultExcludeToday,
      customDate: customDate,
    });
  })();

  // 過去〇〇日のカスタム日数を変更
  yield put(changeKiSyokenCustomDateAction(customDate ?? '1'));

  /**
   * containsAverageについて
   * お気に入り実装時には平均合計行が実装されておらず、その時点で作成したお気に入りは全てcontainsAverageがfalseとして保存されてしまう。
   * しかし、平均合計行が実装された場合には、過去のお気に入りでも平均合計行を表示させるようにしたい。
   * そのため、searchParamsからcontainsAverageを除いたものを取得し、searchParamsへの反映時にcontainsAverageがfalseの場合には省略するようにする。
   *
   * 現在平均合計行はユーザー操作による明示的な表示切り替えがないためこれでも良いが、今後そう言った要件が増える場合にはここの処理の書き換えが必要
   */
  const { containsAverage, ...searchParamsExcludeContainsAverage } =
    favorite.searchParams;

  /** 検索条件の反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenSearchParamsAction({
      ...searchParamsExcludeContainsAverage,
      /** お気に入り実装前の平均合計行を考慮
       * 1. 平均合計行が実装前のお気に入りはcontainsAverage = falseなので、その場合はcontainsAverageを省略する
       *   1-a. 平均合計行が実装された場合は、ここでは反映されずメインのdata Response側のcontainsAverageが参照される
       * 2. 平均合計行が実装されたお気に入りは、そのままの値を反映する
       */
      ...(containsAverage && { containsAverage }),
      ...(() => {
        const params: {
          startDate?: string;
          endDate?: string;
          startComparisonDate?: string;
          endComparisonDate?: string;
          ymdList?: string[];
          ymdComparisonList?: string[];
        } = { ...calcDateRange };

        if (
          convertComparisonDateRange(comparisonSection) !== '平日/土日祝比較'
        ) {
          return params;
        }

        const ymdList = params.ymdList ?? favorite.searchParams.ymdList ?? [];
        const ymdComparisonList =
          params.ymdComparisonList ??
          favorite.searchParams.ymdComparisonList ??
          [];

        if (ymdList.length === 0 && ymdComparisonList.length === 0) {
          return params;
        }

        // 分離済みでも未分離でもマージして UI 状態に揃える（分離は検索時に行う）
        return {
          ...params,
          ...normalizeWeekdayHolidayYmdListsForUiState(
            ymdList,
            ymdComparisonList
          ),
        };
      })(),
    })
  );

  /** 商圏店舗を選択 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenMksHallCodesAction(
      favorite.selectedMksHallCodes ?? []
    )
  );

  /** サブメニュー内数値フィルタの反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenSubMenuNumberFilterAction(
      favorite.subMenuNumberFilter ?? {
        minimumNumber: undefined,
        maximumNumber: undefined,
      }
    )
  );
  /** ヘッダーサブメニューマルチフィルタ（PPM区分など）の反映 */
  const savedMultiFilter = favorite.tableHeaderSubMenuMultiFilter;
  if (savedMultiFilter != null) {
    yield put(
      KiSyokenSettingActionCreators.selectKiSyokenTableHeaderSubMenuMultiFilterAction(
        kiSyokenMultiFilterFromFavorite(savedMultiFilter)
      )
    );
  } else {
    yield put(
      KiSyokenSettingActionCreators.resetKiSyokenTableHeaderSubMenuMultiFilterAction()
    );
  }
  /** 機種名フィルタの反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenNameFilterAction(
      favorite.nameFilter
    )
  );

  /** 非表示項目の反映 */
  yield put(
    KiSyokenSettingActionCreators.applyKiSyokenTableFilterAction(
      favorite.tableFilterItems
    )
  );

  /** 表示項目の列順の反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenColumnsOrderAction(
      favorite.columnsOrder
    )
  );

  /** 機種行への機種タグ表示機能フラグの反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenShowKiTagLabelAction(
      favorite.kiSyokenShowKiTagLabel ?? false
    )
  );

  /** テーブルのチェックボックスで選択されている機種の反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenFavoriteCheckedAllKiListAction(
      favorite.checkedKiList ?? []
    )
  );

  /** テーブルのデータ取得 */
  // 統一フロー: 直接fetchせず、検索アクション経由で送信直前の分離/選択ロジックを適用
  // skipTagAverageRequest: trueでタグ平均リクエストをスキップ（機種絞り込み・機種タグ絞り込みは後で実行するため）
  yield put(
    KiSyokenSettingActionCreators.searchKiSyokenSettingsAction({
      params: {
        ...favorite.searchParams,
        ...calcDateRange,
      },
      dateRangeParams: {
        ...favorite.selectedDateRangeParams,
        dateRange: convertDateRange(originDateRange),
        comparisonSection: convertComparisonDateRange(comparisonSection),
      },
      selectedKiList: favorite.searchParams?.kiList ?? [],
      skipTagAverageRequest: true,
    })
  );

  // メインデータの取得完了を待つ（機種絞り込み・機種タグ絞り込みのfetchで正しいデータを参照するため）
  yield race({
    success: take(DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_SUCCESS),
    failure: take(DataKiSyokenActionTypes.RENEW_DATA_KISYOKEN),
  });

  // メインfetch完了後にタグと機種絞り込みを設定（race condition回避）
  /** タグ絞込で選択されているタグリストの反映 */
  // 機種別商圏のsearchConditionの検索条件を取得
  // 機種タグ情報抽出のため
  const settingsOptionsKiSyokenSearchCondition = yield* customSelect(
    settingsOptionsKiSyokenSearchConditionSelector
  );

  // お気に入り登録時から機種タグ情報が更新された場合の処理
  const findSettingKiTags =
    favorite.kiSyokenKiTagList &&
    settingsOptionsKiSyokenSearchCondition?.kiTagList
      ? (favorite.kiSyokenKiTagList
          .map((kiTag) =>
            settingsOptionsKiSyokenSearchCondition.kiTagList.find(
              (item) => kiTag.tagId === item.tagId
            )
          )
          .filter((findItem) => findItem !== undefined) as KiTagListOptions[])
      : [];

  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenKiTagListAction(
      findSettingKiTags
    )
  );

  /** 機種絞り込みの反映 */
  yield put(
    KiSyokenSettingActionCreators.selectKiSyokenSelectKiListFilterAction(
      favorite.kiListFilter ?? []
    )
  );

  /** 機種絞り込みのデータ取得 */
  if (
    Array.isArray(favorite.kiListFilter) &&
    favorite.kiListFilter.length > 0
  ) {
    // 統一フロー: searchActionKiListAverageSaga経由で期間再計算を適用
    yield put(
      KiSyokenSettingActionCreators.searchActionKiListAverageAction(
        {
          ...favorite.searchParams,
          ...calcDateRange,
        },
        favorite.kiListFilter // 取得対象の機種番号を渡す
      )
    );
  }

  /** 機種タグ絞り込みのデータ取得 */
  if (
    Array.isArray(favorite.kiSyokenKiTagList) &&
    favorite.kiSyokenKiTagList.length > 0
  ) {
    // 期間再計算付きで機種タグの平均合計を取得。
    // お気に入りの searchParams には保存時点の種別が既に含まれるため、
    // 種別を解決し直さずそのまま解決済みとして渡す（元のオブジェクト形状を維持）。
    yield put(
      searchActionKiTagAverageOnSearchConditionChangeAction(
        asKiSyokenResolvedSearchParams({
          ...favorite.searchParams,
          ...calcDateRange,
        }),
        favorite.kiSyokenKiTagList
      )
    );
  }

  if (
    favorite.kasidamaTable.searchParams &&
    Object.keys(favorite.kasidamaTable.searchParams).length !== 0
  ) {
    /** チェーン店一覧の検索条件を変更 */
    const {
      startDate,
      endDate,
      startComparisonDate,
      endComparisonDate,
      ...restKasidamaParams
    } = favorite.kasidamaTable.searchParams;
    yield put(
      KiSyokenSettingActionCreators.selectKiSyokenKasidamaSearchParamsAction({
        ...restKasidamaParams,
        ...(startDate !== null && { startDate }),
        ...(endDate !== null && { endDate }),
        ...(startComparisonDate !== null && { startComparisonDate }),
        ...(endComparisonDate !== null && { endComparisonDate }),
      })
    );

    /** チェーン店一覧のデータ取得 */
    // 統一フロー: 直接fetchせず、検索アクション経由で送信直前のフィルタリングを適用
    const restoredKiCode =
      favorite.kasidamaTable.searchParams?.kiList?.[0] ??
      favorite.searchParams?.kiList?.[0];
    if (restoredKiCode != null) {
      yield put(
        KiSyokenSettingActionCreators.searchKiSyokenKasidamaAction(
          restoredKiCode
        )
      );
    }
    /**チェーン店の並び順を変更 */
    yield put(
      KiSyokenSettingActionCreators.selectKiSyokenKasidamaColumnsOrderAction(
        favorite.kasidamaTable.columnsOrder
      )
    );
    /** 派生テーブルを開く（お気に入りに保存された検索条件がある場合） */
    yield put(KiSyokenSettingActionCreators.openKiSyokenKasidamaAction());
  }
}

/**
 * お気に入り上書き保存時の処理
 */
function* saveFavoriteSaga(action: SaveKiSyokenFavoriteAction) {
  /** 選択中のお気に入りID */
  const selectedFavoriteId = yield* customSelect(
    kiSyokenSelectedFavoriteSelector
  );
  /** 選択中のお気に入り */
  const selectedFavorite = yield* customSelect(
    kiSyokenSelectedFavoriteDataSelector
  );

  /** 現在の機種別商圏の設定すべて */
  const pageSetting = yield* customSelect(kiSyokenSettingSelector);

  /** 機種名フィルタ */
  const modelNameFilter = yield* customSelect(kiSyokenNameFilterSelector);

  /** サブメニュー内数値フィルタ*/
  const subMenuNumberFilter = yield* customSelect(
    kiSyokenSubMenuNumberFilterSelector
  );

  /** ヘッダーサブメニューマルチフィルタ（PPM区分など） */
  const tableHeaderSubMenuMultiFilter = yield* customSelect(
    kiSyokenTableHeaderSubMenuMultiFilterSelector
  );

  /** テーブルフィルターで選択中の項目 */
  const tableFilterSelectedItems = yield* customSelect(
    kiSyokenSelectedTableFilterSelector
  );

  /** 商圏コード */
  const selectedMksHallCodes = yield* customSelect(
    kiSyokenMksHallCodesSelector
  );

  /** チェーン店一覧の列順を取得する */
  const selectedKasidamaColumnsOrder = yield* customSelect(
    kiSyokenKasidamaColumnsOrderSelector
  );

  // タグ絞込で選択されているタグリスト
  const kiSyokenKiTagList = yield* customSelect(kiSyokenTagListSelector);

  // 機種行への機種タグ表示機能フラグ
  const kiSyokenShowKiTagLabel = yield* customSelect(
    kiSyokenShowKiTagLabelSelector
  );
  /** 過去xx日のカスタム日数を取得する */
  const customDate = yield* customSelect(kiSyokenCustomDateSelector);

  // 機種絞り込み
  const kiListFilter = yield* customSelect(kiSyokenListFilterSelector);

  // テーブルのチェックボックスで選択されている機種
  const checkedKiList = yield* customSelect(
    kiSyokenSelectCheckedKiListSelector
  );

  /** デフォルトの時は動作しない */
  if (selectedFavoriteId === undefined) return;

  let favorite: FavoriteItem = {};
  if (action.payload.mode === 'memo') {
    /** メモのみ更新モードでの変更後のお気に入りデータ */
    favorite = {
      ...selectedFavorite,
      memo: action.payload.memo,
    };
  } else {
    /** 変更後のお気に入りデータ */
    favorite = {
      name: action.payload.name,
      isShared: action.payload.isShared,
      pageSetting: {
        kiSyoken: {
          // 期間情報を含む完全なsearchParamsを保存
          searchParams: {
            ...pageSetting.searchParams,
            // UIで表示されている期間情報を明示的に含める
            startDate: pageSetting.searchParams.startDate,
            endDate: pageSetting.searchParams.endDate,
            startComparisonDate: pageSetting.searchParams.startComparisonDate,
            endComparisonDate: pageSetting.searchParams.endComparisonDate,
            ymdList: pageSetting.searchParams.ymdList,
            ymdComparisonList: pageSetting.searchParams.ymdComparisonList,
          },
          kasidamaTable: {
            // 派生テーブルが開かれている場合のみ検索条件を保存する
            ...(pageSetting.kasidamaTable?.isOpen && {
              searchParams: pageSetting.kasidamaTable?.searchParams,
            }),
            columnsOrder: selectedKasidamaColumnsOrder,
          },
          selectedDateRangeParams: pageSetting.selectedDateRangeParams,
          columnsOrder: pageSetting.columnsOrder,
          nameFilter: modelNameFilter,
          subMenuNumberFilter: subMenuNumberFilter,
          tableHeaderSubMenuMultiFilter: tableHeaderSubMenuMultiFilter,
          tableFilterItems: tableFilterSelectedItems,
          selectedMksHallCodes: selectedMksHallCodes,
          kiSyokenKiTagList: kiSyokenKiTagList,
          kiSyokenShowKiTagLabel: kiSyokenShowKiTagLabel,
          kiListFilter: kiListFilter,
          checkedKiList: checkedKiList,
          customDate: customDate,
        },
      },
      memo: action.payload.memo,
      privatelySharedUsers: action.payload.sharedUser,
    };
  }

  /** お気に入りを上書き */
  yield put(
    SettingsFavoritesActionCreators.patchSettingsFavoritesAction(
      selectedFavoriteId,
      favorite,
      { pageName: '機種別商圏データ' }
    )
  );
}

/**
 * お気に入りに保存するデータの生成
 */
function* setPageSetting() {
  /** 現在の機種別商圏データの設定すべて */
  const pageSetting = yield* customSelect(kiSyokenSettingSelector);

  /** 機種名フィルタ */
  const nameFilter = yield* customSelect(kiSyokenNameFilterSelector);

  /** サブメニュー内数値フィルタ */
  const subMenuNumberFilter = yield* customSelect(
    kiSyokenSubMenuNumberFilterSelector
  );

  /** ヘッダーサブメニューマルチフィルタ（PPM区分など） */
  const tableHeaderSubMenuMultiFilterForSave = yield* customSelect(
    kiSyokenTableHeaderSubMenuMultiFilterSelector
  );

  /** テーブルフィルターで選択中の項目 */
  const tableFilterSelectedItems = yield* customSelect(
    kiSyokenSelectedTableFilterSelector
  );

  /** 商圏コード */
  const selectedMksHallCodes = yield* customSelect(
    kiSyokenMksHallCodesSelector
  );

  /** チェーン店一覧の列順を取得する */
  const selectedKasidamaColumnsOrder = yield* customSelect(
    kiSyokenKasidamaColumnsOrderSelector
  );

  // タグ絞込で選択されているタグリスト
  const kiSyokenKiTagList = yield* customSelect(kiSyokenTagListSelector);

  // 機種行への機種タグ表示機能フラグ
  const kiSyokenShowKiTagLabel = yield* customSelect(
    kiSyokenShowKiTagLabelSelector
  );
  /** 過去xx日のカスタム日数を取得する */
  const customDate = yield* customSelect(kiSyokenCustomDateSelector);

  // 機種絞り込み
  const kiListFilter = yield* customSelect(kiSyokenListFilterSelector);

  // テーブルのチェックボックスで選択されている機種
  const checkedKiList = yield* customSelect(
    kiSyokenSelectCheckedKiListSelector
  );
  const searchParamsForFavorite = pageSetting.searchParams;

  const result: FavoriteItem['pageSetting'] = {
    kiSyoken: {
      searchParams: searchParamsForFavorite,
      kasidamaTable: {
        // 派生テーブルが開かれている場合のみ検索条件を保存する
        ...(pageSetting.kasidamaTable?.isOpen && {
          searchParams: pageSetting.kasidamaTable.searchParams,
        }),
        columnsOrder: selectedKasidamaColumnsOrder,
      },
      selectedDateRangeParams: pageSetting.selectedDateRangeParams,
      columnsOrder: pageSetting.columnsOrder,
      nameFilter: nameFilter,
      subMenuNumberFilter: subMenuNumberFilter,
      tableHeaderSubMenuMultiFilter: tableHeaderSubMenuMultiFilterForSave,
      tableFilterItems: tableFilterSelectedItems,
      selectedMksHallCodes: selectedMksHallCodes,
      kiSyokenKiTagList: kiSyokenKiTagList,
      kiSyokenShowKiTagLabel: kiSyokenShowKiTagLabel,
      kiListFilter: kiListFilter,
      checkedKiList: checkedKiList,
      customDate: customDate,
    },
  };
  return result;
}

/**
 * お気に入り新規保存時の処理
 */
function* saveAsFavoriteSaga(action: SaveAsKiSyokenFavoriteAction) {
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  // 変更後のお気に入りデータ
  const favorite: FavoriteItem = {
    name: action.payload.name,
    isShared: action.payload.isShared,
    pageName: '機種別商圏データ',
    pageSetting,
    memo: action.payload.memo,
    privatelySharedUsers: action.payload.sharedUser,
  };

  // お気に入りを新規保存
  yield put(
    SettingsFavoritesActionCreators.postSettingsFavoritesAction([favorite], {})
  );
}

/**
 * お気に入り関連の処理
 */
function* handleFavoriteSaga() {
  // お気に入り適用
  yield takeEvery(
    KiSyokenSettingActionTypes.CHANGE_KISYOKEN_FAVORITE,
    applyFavoriteByIdSaga
  );
  // お気に入り上書き保存
  yield takeEvery(
    KiSyokenSettingActionTypes.SAVE_KISYOKEN_FAVORITE,
    saveFavoriteSaga
  );
  // お気に入り新規保存
  yield takeEvery(
    KiSyokenSettingActionTypes.SAVE_AS_KISYOKEN_FAVORITE,
    saveAsFavoriteSaga
  );
}

// 画面共有用短縮URL作成の処理
function* handleShortenedUrlSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.CREATE_KISYOKEN_SHORTENED_URL,
    createShortenedUrlSaga
  );
}

/**
 * 平均合計行データ取得（期間再計算付き）の処理
 */
function* handleSearchKiListAverageSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_KISYOKEN_AVERAGE_SETTINGS,
    searchActionKiListAverageSaga
  );
}

/**
 * タグ別平均合計行データ取得（期間再計算付き）の処理
 */
function* handleSearchKiTagAverageSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_KISYOKEN_KI_TAG_AVERAGE_SETTINGS,
    searchActionKiTagAverageSaga
  );
}

/**
 * タグ別平均合計行データ取得（検索条件変更時）の処理
 */
function* handleSearchKiTagAverageOnSearchConditionChangeSaga() {
  yield takeEvery(
    KiSyokenSettingActionTypes.SEARCH_KISYOKEN_KI_TAG_AVERAGE_ON_SEARCH_CONDITION_CHANGE_SETTINGS,
    searchActionKiTagAverageOnSearchConditionChangeSaga
  );
}

/**
 * 画面共有用短縮URL作成の処理
 */
export function* createShortenedUrlSaga(
  action: CreateKiSyokenShortenedUrlAction
) {
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  const compressed = compressToEncodedURIComponent(
    action.payload.pageName,
    pageSetting ?? {}
  );
  const originalUrl = `${action.payload.locationUrl}?q=${compressed}`;

  yield put(ShortenedUrlActionCreators.postShortenedUrlAction({ originalUrl }));
}

export function* kiSyokenSettingSaga() {
  yield fork(handleInitFetchSaga);
  yield fork(handleChangeFieldSaga);
  yield fork(handleFieldOrderSaga);
  yield fork(handleChangeCurrentHallsSaga);
  yield fork(handleChangeCurrentShuSaga);
  yield fork(handleDateRangeSlideSaga);
  yield fork(handleSearchSaga);
  yield fork(handleSearchResetSaga);
  yield fork(handleFavoriteSaga);
  yield fork(handleShortenedUrlSaga);
  yield fork(handleSearchKiListAverageSaga);
  yield fork(handleSearchKiTagAverageSaga);
  yield fork(handleSearchKiTagAverageOnSearchConditionChangeSaga);
}
